import React, { useState, useEffect } from 'react';
import { Gift, Plus, Edit2, Trash2, Search, Package, TrendingUp, TrendingDown, X } from 'lucide-react';
import api, { API_URL } from '../../services/api';

function StockPackaging() {
  const [packaging, setPackaging] = useState([]);
  const [filteredPackaging, setFilteredPackaging] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingPackaging, setEditingPackaging] = useState(null);
  const [showMouvementModal, setShowMouvementModal] = useState(false);
  const [selectedPackaging, setSelectedPackaging] = useState(null);
  const [formData, setFormData] = useState({
    type: '', prix_ht: '', prix_ttc: '', majoration: ''
  });
  const [formMouvement, setFormMouvement] = useState({
    type_mouvement: 'entree',
    quantite: '',
    motif: '',
    prix_unitaire: '',
    notes: ''
  });

  useEffect(() => {
    loadPackaging();
  }, []);

  useEffect(() => {
    filterPackaging();
  }, [packaging, searchTerm]);

  const loadPackaging = async () => {
    try {
      const response = await api.get(`${API_URL}/packaging`);
      setPackaging(response.data || []);
    } catch (error) {
      console.error('Erreur chargement:', error);
    }
  };

  const filterPackaging = () => {
    let filtered = packaging;

    if (searchTerm) {
      filtered = filtered.filter(p =>
        p.type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredPackaging(filtered);
  };

  const handleOpenModal = (pack = null) => {
    if (pack) {
      setEditingPackaging(pack);
      setFormData({
        type: pack.type || '',
        prix_ht: pack.prix_ht != null ? String(pack.prix_ht) : '',
        prix_ttc: pack.prix_ttc != null ? String(pack.prix_ttc) : '',
        majoration: pack.majoration != null ? String(pack.majoration) : '',
      });
    } else {
      setEditingPackaging(null);
      setFormData({ type: '', prix_ht: '', prix_ttc: '', majoration: '' });
    }
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      const payload = {
        type: formData.type,
        prix_ht: formData.prix_ht !== '' ? parseFloat(formData.prix_ht) : 0,
        prix_ttc: formData.prix_ttc !== '' ? parseFloat(formData.prix_ttc) : 0,
        majoration: formData.majoration !== '' ? parseFloat(formData.majoration) : 0,
      };
      if (editingPackaging) {
        await api.put(`${API_URL}/packaging/${editingPackaging.id}`, payload);
        alert('Packaging modifié avec succès');
      } else {
        await api.post(`${API_URL}/packaging`, payload);
        alert('Packaging créé avec succès');
      }
      setShowModal(false);
      await loadPackaging();
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce packaging ?')) return;
    
    try {
      await api.delete(`${API_URL}/packaging/${id}`);
      alert('Packaging supprimé avec succès');
      await loadPackaging();
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const handleOpenMouvement = (pack) => {
    setSelectedPackaging(pack);
    setFormMouvement({
      type_mouvement: 'entree',
      quantite: '',
      motif: '',
      prix_unitaire: pack.prix_unitaire || '',
      notes: ''
    });
    setShowMouvementModal(true);
  };

  const handleSaveMouvement = async () => {
    try {
      await api.post(`${API_URL}/packaging/${selectedPackaging.id}/mouvement`, formMouvement);
      alert('Mouvement enregistré avec succès');
      setShowMouvementModal(false);
      await loadPackaging();
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de l\'enregistrement du mouvement');
    }
  };

  const getStockColor = (stock, minimum) => {
    if (stock <= 0) return 'text-red-600 font-bold';
    if (stock <= minimum) return 'text-orange-600 font-bold';
    return 'text-green-600 font-bold';
  };

  return (
    <div className="space-y-6">
      {/* Filtres et actions */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="flex gap-4 items-center">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Rechercher un packaging..."
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
            />
          </div>

          <button
            onClick={() => handleOpenModal()}
            className="px-6 py-2 bg-gradient-to-r from-green-600 to-teal-600 text-white rounded-lg hover:from-green-700 hover:to-teal-700 transition-all shadow-lg flex items-center gap-2 font-semibold"
          >
            <Plus className="h-5 w-5" />
            Nouveau Packaging
          </button>
        </div>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="text-sm text-gray-600 mb-1">Total Types</div>
          <div className="text-2xl font-bold text-green-600">{packaging.length}</div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="text-sm text-gray-600 mb-1">En Stock</div>
          <div className="text-2xl font-bold text-green-600">
            {packaging.filter(p => p.stock_actuel > 0).length}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="text-sm text-gray-600 mb-1">Stock Faible</div>
          <div className="text-2xl font-bold text-orange-600">
            {packaging.filter(p => p.stock_actuel > 0 && p.stock_actuel <= p.stock_minimum).length}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="text-sm text-gray-600 mb-1">Rupture</div>
          <div className="text-2xl font-bold text-red-600">
            {packaging.filter(p => p.stock_actuel <= 0).length}
          </div>
        </div>
      </div>

      {/* Table du packaging */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stock</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Prix HT</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Prix TTC</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Majoration</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fournisseur</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredPackaging.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                  <Gift className="h-12 w-12 mx-auto mb-2 opacity-30" />
                  <p>Aucun packaging trouvé</p>
                </td>
              </tr>
            ) : (
              filteredPackaging.map((pack) => (
                <tr key={pack.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">{pack.type}</td>
                  <td className="px-6 py-4 text-sm">
                    {parseFloat(pack.stock_actuel || 0) > 0 ? (
                      <>
                        <span className={getStockColor(pack.stock_actuel, pack.stock_minimum)}>
                          {parseInt(pack.stock_actuel || 0)}
                        </span>
                        {pack.stock_minimum > 0 && (
                          <span className="text-xs text-gray-500 ml-1">
                            (min: {pack.stock_minimum})
                          </span>
                        )}
                      </>
                    ) : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 text-right">{parseFloat(pack.prix_ht || 0).toFixed(2)} DH</td>
                  <td className="px-6 py-4 text-sm font-semibold text-gray-900 text-right">{parseFloat(pack.prix_ttc || 0).toFixed(2)} DH</td>
                  <td className="px-6 py-4 text-sm text-amber-700 text-right">{parseFloat(pack.majoration || 0).toFixed(2)} DH</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{pack.fournisseur || '-'}</td>
                  <td className="px-6 py-4 text-sm">
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleOpenMouvement(pack)}
                        className="p-1 text-green-600 hover:bg-green-50 rounded"
                        title="Gérer stock"
                      >
                        <Package className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleOpenModal(pack)}
                        className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                        title="Modifier"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(pack.id)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                        title="Supprimer"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal Ajouter/Modifier Packaging */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl">
            <div className="flex justify-between items-center p-6 border-b bg-gradient-to-r from-green-600 to-teal-600 text-white">
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <Gift className="h-6 w-6" />
                {editingPackaging ? 'Modifier Packaging' : 'Nouveau Packaging'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-white/20 rounded-full transition-all"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
                <input
                  type="text"
                  value={formData.type}
                  onChange={(e) => setFormData({...formData, type: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                  placeholder="Ex: Écrin Bague"
                />
              </div>

              {/* 12/06/2026 : le bareme vit dans la table `packaging` (HT/TTC/majoration).
                  Le stock s'ajuste via le bouton mouvement (📦), plus depuis cette modale. */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prix HT</label>
                  <input
                    type="text" inputMode="decimal" onFocus={e => e.target.select()}
                    value={formData.prix_ht}
                    onChange={(e) => setFormData({...formData, prix_ht: e.target.value.replace(',', '.')})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prix TTC</label>
                  <input
                    type="text" inputMode="decimal" onFocus={e => e.target.select()}
                    value={formData.prix_ttc}
                    onChange={(e) => setFormData({...formData, prix_ttc: e.target.value.replace(',', '.')})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Majoration</label>
                  <input
                    type="text" inputMode="decimal" onFocus={e => e.target.select()}
                    value={formData.majoration}
                    onChange={(e) => setFormData({...formData, majoration: e.target.value.replace(',', '.')})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-400">La majoration est le montant utilisé par le moteur de prix (PVP).</p>
            </div>

            <div className="p-6 border-t bg-gray-50 flex justify-end gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-6 py-2 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded-lg font-semibold transition-all"
              >
                Annuler
              </button>
              <button
                onClick={handleSave}
                className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition-all"
              >
                {editingPackaging ? 'Enregistrer' : 'Créer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Mouvement Stock */}
      {showMouvementModal && selectedPackaging && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="flex justify-between items-center p-6 border-b bg-gradient-to-r from-green-600 to-teal-600 text-white">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Package className="h-6 w-6" />
                Mouvement de Stock
              </h2>
              <button
                onClick={() => setShowMouvementModal(false)}
                className="p-2 hover:bg-white/20 rounded-full transition-all"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-green-50 rounded-lg p-3 mb-4">
                <div className="font-bold text-gray-800">{selectedPackaging.type}</div>
                <div className="text-sm text-gray-600">Stock actuel: {parseInt(selectedPackaging.stock_actuel || 0)} pièces</div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setFormMouvement({...formMouvement, type_mouvement: 'entree'})}
                  className={`p-3 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 ${
                    formMouvement.type_mouvement === 'entree'
                      ? 'bg-green-600 text-white shadow-lg'
                      : 'bg-white text-gray-700 border-2 border-green-300 hover:bg-green-50'
                  }`}
                >
                  <TrendingUp className="h-5 w-5" />
                  Entrée
                </button>
                <button
                  onClick={() => setFormMouvement({...formMouvement, type_mouvement: 'sortie'})}
                  className={`p-3 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 ${
                    formMouvement.type_mouvement === 'sortie'
                      ? 'bg-red-600 text-white shadow-lg'
                      : 'bg-white text-gray-700 border-2 border-red-300 hover:bg-red-50'
                  }`}
                >
                  <TrendingDown className="h-5 w-5" />
                  Sortie
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Quantité *</label>
                <input
                  type="text" inputMode="decimal" onFocus={e => e.target.select()}
                  value={formMouvement.quantite}
                  onChange={(e) => setFormMouvement({...formMouvement, quantite: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Motif *</label>
                <input
                  type="text"
                  value={formMouvement.motif}
                  onChange={(e) => setFormMouvement({...formMouvement, motif: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                  placeholder="Ex: Achat fournisseur, Utilisation commande"
                />
              </div>

              {formMouvement.type_mouvement === 'entree' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prix Unitaire</label>
                  <input
                    type="text" inputMode="decimal" onFocus={e => e.target.select()}
                    step="0.01"
                    value={formMouvement.prix_unitaire}
                    onChange={(e) => setFormMouvement({...formMouvement, prix_unitaire: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={formMouvement.notes}
                  onChange={(e) => setFormMouvement({...formMouvement, notes: e.target.value})}
                  rows="2"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                />
              </div>
            </div>

            <div className="p-6 border-t bg-gray-50 flex justify-end gap-3">
              <button
                onClick={() => setShowMouvementModal(false)}
                className="px-6 py-2 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded-lg font-semibold transition-all"
              >
                Annuler
              </button>
              <button
                onClick={handleSaveMouvement}
                className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition-all"
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default StockPackaging;
